/**
 * Interaction Logging API
 *
 * Dedicated module for interaction logging. The core hooks (useAddInteractionLog,
 * useInteractionLogs) live in residentsApi.ts and are re-exported here for
 * convenience along with location/type constants.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient, { apiHeaders, apiUrl } from './client';
import { API } from './endpoints';
import { useGlobalStore } from '../stores/globalStore';

// Re-export the core hooks so consumers can import from this module
export { useAddInteractionLog, useInteractionLogs, useSystemLogs, HISTORY_QUERY_FRESHNESS } from './residentsApi';
import { useAddInteractionLog, HISTORY_QUERY_FRESHNESS } from './residentsApi';

// Re-export the type
export type { LogInteraction } from '../types/patient';
import type { LogInteraction } from '../types/patient';

/**
 * Logs a View interaction on component mount. Handles React 18 strict-mode
 * double-fire via cancelled flag + deferred timeout.
 *
 * `notes` is for a location several screens share, where the row is only
 * readable if it says which one wrote it - FacilityDataGrid covers every
 * facility-level editor, so WinForms stamps each with its own note
 * ("Nutritional Analysis", and so on). Most callers leave it blank.
 */
export function useLogView(
  facilityId: number,
  location: number,
  options?: { patientId?: number; enabled?: boolean; notes?: string },
) {
  const addLog = useAddInteractionLog(facilityId);
  const notes = options?.notes ?? '';
  useEffect(() => {
    let cancelled = false;
    if (facilityId > 0 && options?.enabled !== false) {
      const timer = setTimeout(() => {
        if (!cancelled) {
          addLog.mutate({
            fKey_Log_Interaction_Location: location,
            fKey_Log_Interaction_Type: 4, // View
            ...(options?.patientId ? { fKey_Patient: options.patientId } : {}),
            notes,
          });
        }
      }, 0);
      return () => { cancelled = true; clearTimeout(timer); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, location, options?.patientId, notes]);
}

/**
 * Logs ONE summary Modify-type interaction when the user navigates
 * away from a screen that allowed CRUD edits — replaces the
 * per-action logging pattern with a single "this user did stuff
 * on this screen" entry. Individual row-level changes are
 * captured in dedicated snapshot log tables (tblLog_Prd_Meal_Pattern
 * etc.) separately.
 *
 * Behavior:
 *   • Call `markDirty()` after every successful Add/Modify/Delete
 *     handler. Marking is cheap and idempotent; once set, stays
 *     set until the component unmounts.
 *   • On component unmount, if dirty was set, POST one Modify
 *     log via `useAddInteractionLog`. Patient identity is
 *     captured at hook setup (stored in refs) so unmount-time
 *     closures see the right values regardless of re-renders.
 *   • On browser close / refresh, `pagehide`/`beforeunload`
 *     sends a keepalive fetch so the log survives navigation
 *     where a normal request wouldn't.
 *
 * Mounting/unmounting the same component multiple times across a
 * session creates a new log per "session" of the screen — that
 * matches the WinForms behavior of one log per dialog-open.
 */
export function useLogModifyOnUnmount(
  facilityId: number,
  location: number,
  ctx: { patientId?: number; firstName?: string; lastName?: string },
) {
  // User identity from the global store — must travel on the
  // request so the backend can write it into tblLog_Interaction
  // alongside the patient context.
  const userId = useGlobalStore((s) => s.userId);
  const userName = useGlobalStore((s) => s.userName);
  const userFirstName = useGlobalStore((s) => s.userFirstName);
  const userLastName = useGlobalStore((s) => s.userLastName);

  // Refs so the unmount cleanup sees current values, not stale
  // closures from the initial render.
  const dirtyRef = useRef(false);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const userRef = useRef({ userId, userName, userFirstName, userLastName });
  userRef.current = { userId, userName, userFirstName, userLastName };

  // Prevent double-fire under React strict-mode double-mount and
  // when both unmount + pagehide fire in close succession.
  const firedRef = useRef(false);

  const markDirty = useCallback(() => {
    dirtyRef.current = true;
  }, []);

  // Build the payload at fire time so it reflects the latest
  // patient + user identity. Shared between unmount cleanup and
  // the pagehide beacon path.
  const buildPayload = () => ({
    fKey_Log_Interaction_Location: location,
    fKey_Log_Interaction_Type: INTERACTION_TYPE.Modify,
    fKey_Patient: ctxRef.current.patientId ?? undefined,
    residentFirstName: ctxRef.current.firstName ?? '',
    residentLastName: ctxRef.current.lastName ?? '',
    pmSec_User_PKey_User: userRef.current.userId,
    pmSec_User_UserName: userRef.current.userName,
    pmSec_User_FirstName: userRef.current.userFirstName,
    pmSec_User_LastName: userRef.current.userLastName,
    notes: '',
  });

  // Browser-close / refresh path — `pagehide` is more reliably
  // fired than `beforeunload` on mobile and bfcache navigations.
  // A keepalive fetch is delivered during unload like sendBeacon, and
  // unlike sendBeacon it can carry the auth and connection headers the
  // API requires.
  useEffect(() => {
    const handler = () => {
      if (!dirtyRef.current || firedRef.current || facilityId <= 0) return;
      firedRef.current = true;
      void fetch(apiUrl(API.logInteractionAdd(facilityId)), {
        method: 'POST',
        keepalive: true,
        headers: { ...apiHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      }).catch(() => {
        // Best effort: the page is going away.
      });
    };
    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
    // buildPayload reads from refs, so we only re-bind when
    // facilityId/location change (rare).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, location]);

  // SPA-unmount path — runs when the user navigates to a
  // different tab/route inside the app. apiClient.post is fire-
  // and-forget; we don't await because the component is going
  // away. Marking firedRef prevents the pagehide path from
  // double-logging if a navigation immediately precedes a close.
  useEffect(() => {
    return () => {
      if (!dirtyRef.current || firedRef.current || facilityId <= 0) return;
      firedRef.current = true;
      // Fire-and-forget intentionally; no error surfacing.
      apiClient.post(API.logInteractionAdd(facilityId), buildPayload()).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, location]);

  return { markDirty };
}

// ─── Interaction Log Details (Patient Profile expand) ────
//
// Composite per-row detail payload — one fetch per patient
// gives every SM item, MP row change, and Adj row change over
// the last 30 days. Server does the view→modify pairing so the
// client just looks each master row up in the returned dicts:
//   SM  keyed by fKey_Log_Transaction (on the master row)
//   MP  keyed by fKey_Log_Interaction (populated server-side)
//   Adj keyed by fKey_Log_Interaction (populated server-side)
export type InteractionLogRowDict = Record<string, unknown>;
export interface InteractionLogDetails {
  sm: InteractionLogRowDict[];
  mp: InteractionLogRowDict[];
  adj: InteractionLogRowDict[];
}
// Wire keys are lowercased by System.Text.Json's
// `FirstCharLowerPolicy` (Program.cs:836). The DTO's
// `[JsonProperty("SM")]` is a Newtonsoft attribute — STJ IGNORES it
// and applies the policy: all-uppercase runs become lowercase (SM
// → sm, MP → mp), mixed-case leaves the tail alone (Adj → adj).
// Reading `sM` / `mP` — the naïve camelCase guess — returns
// undefined every time, which is why Adj worked (matched by
// coincidence) and SM/MP always came back empty.
interface RawInteractionLogDetails {
  sm?: InteractionLogRowDict[];
  mp?: InteractionLogRowDict[];
  adj?: InteractionLogRowDict[];
}
export function useInteractionLogDetails(facilityId: number, patientId: number, days: number = 30) {
  return useQuery<InteractionLogDetails>({
    queryKey: ['interactionLogs', 'details', facilityId, patientId, days] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<RawInteractionLogDetails>(
        API.logInteractionDetails(facilityId, patientId, days),
      );
      return {
        sm: data?.sm ?? [],
        mp: data?.mp ?? [],
        adj: data?.adj ?? [],
      };
    },
    enabled: facilityId > 0 && patientId > 0,
    // History is never cached — see HISTORY_QUERY_FRESHNESS.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

/**
 * Fetch interaction logs for a specific entity (diet type, consistency, etc.)
 * Uses the entity-type dispatch in LogInteractionDAL.
 */
export function useEntityLogs(facilityId: number, logEntityType: number, entityId: number) {
  return useQuery<LogInteraction[]>({
    queryKey: ['interactionLogs', 'entity', logEntityType, entityId, facilityId],
    queryFn: async () => {
      const { data } = await apiClient.get<LogInteraction[]>(
        API.logInteractions(facilityId, logEntityType, entityId),
      );
      return data;
    },
    enabled: facilityId > 0 && entityId > 0 && logEntityType > 0,
    // History is never cached — see HISTORY_QUERY_FRESHNESS.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

/**
 * InteractionLocation constants - matches InteractionLogEnums.cs / InteractionLocation enum.
 * Use these instead of magic numbers when logging interactions.
 */
export const INTERACTION_LOCATION = {
  Assessments: 1,
  CalorieProteinFluidNeeds: 2,
  DietFluctuations: 3,
  IntakeStudy: 4,
  LabData: 5,
  MealPattern: 6,
  Medications: 7,
  Notes: 8,
  ProblemGoalApproach: 9,
  Recommendations: 10,
  Skin: 11,
  TubeFeed: 12,
  WeightHistory: 13,
  WeightEntry: 14,
  SelectiveMenu: 15,
  SelectiveMenuTouch: 16,
  ResidentAddMod: 17,
  ResidentDataGrid: 18,
  TrayLine: 19,
  ManageResidents: 20,
  TubeFeedLabel: 21,
  MenuCycle: 22,
  ItemDataGrid: 23,
  ItemAddMod: 24,
  GlobalMealPattern: 25,
  GlobalMealPatternAddMod: 26,
  GlobalMealPatternGroupDataGrid: 27,
  GlobalAdjustmentGroupDataGrid: 28,
  GlobalMealPatternGroupAddMod: 29,
  GlobalAdjustmentGroupAddMod: 30,
  GlobalAdjustmentAddMod: 31,
  FoodAllergyDataGrid: 32,
  FoodAllergyAddMod: 33,
  DietTypeDatagrid: 34,
  DietTypeAddMod: 35,
  ConsistencyDatagrid: 36,
  ConsistencyAddMod: 37,
  SpecialDevicesDataGrid: 38,
  SpecialDevicesAddMod: 39,
  SeatingAssignmentDataGrid: 40,
  SeatingAssignmentAddMod: 41,
  GuestAddMod: 42,
  GuestQuickAdd: 43,
  DietechDataCleanup: 44,
  RecipeDataGrid: 45,
  RecipeAddMod: 46,
  FacilityUnitAddMod: 47,
  AllergyGroupDataGrid: 48,
  AllergyGroupAddMod: 49,
  FoodGroupDataGrid: 50,
  FacilityDataGrid: 51,
  FacilityAddMod: 52,
  ItemTypeDataGrid: 53,
  ItemTypeAddMod: 54,
  ServiceItemDataGrid: 55,
  ServiceItemAddMod: 56,
  CreditsAndBilling: 57,
  ItemGroupModify: 58,
  BillingCodeAddMod: 59,
  SpecialNeedsDataGrid: 60,
  SpecialNeedsAddMod: 61,
  FacilityFeedingStatusDataGrid: 62,
  FacilityFeedingStatusAddMod: 63,
  ReportDashboard: 64,
  EHR: 65,
  TrayTicketVisualizer: 66,
  FoodGroupAddMod: 67,
  NotesDataGrid: 68,
  NotesAddMod: 69,
  EHRMappingDataGrid: 70,
  EHRMessageViewer: 71,
  ShelfLocation: 72,
  ItemShelfLocationDataGrid: 73,
  OrderGuideDataGrid: 74,
  OrderGuideAddMod: 75,
  OrderGuideAssignment_DataGrid: 76,
  Item_Linking_Datagrid: 77,
  Portion_AddMod: 78,
  DRIGroup_DataGrid: 79,
  DRIGroup_AddMod: 80,
  DiningRoom_DataGrid: 81,
  DiningRoom_AddMod: 82,
  Analysis: 87,
  RecipeDataUpdate: 88,
  TubeProduct_DataGrid: 89,
  TubeProduct_AddMod: 90,
  MenuCycleOverride: 91,
  Employee_DataGrid: 92,
  NoteType_DataGrid: 93,
  NoteType_AddMod: 94,
  MedicationEffect_DataGrid: 95,
  MedicationEffect_AddMod: 96,
  MenuCategory_DataGrid: 97,
  MenuCategory_AddMod: 98,
  Doctor_DataGrid: 99,
  Doctor_AddMod: 100,
  PrinterCategory_DataGrid: 101,
  PrinterCategory_AddMod: 102,
  MaterialColor_DataGrid: 103,
  MaterialColor_AddMod: 104,
  Garnish_DataGrid: 105,
  Garnish_AddMod: 106,
  ProductionArea_DataGrid: 107,
  ProductionArea_AddMod: 108,
  ItemUnit_DataGrid: 109,
  ItemUnit_AddMod: 110,
  SurveyTemplateDataGrid: 111,
  SurveyTemplateAddMod: 112,
  SurveySessionDataGrid: 113,
  SurveySessionAddMod: 114,
  SurveyResponseEntry: 115,
  SurveyAnalysis: 116,
  // Kitchen Display System (KDS)
  KitchenBump: 117,
  KitchenCancel: 118,
  KitchenServeEarly: 119,
  KitchenCompleteItem: 120,
  KitchenDeliveryAssign: 121,
  KitchenDeliveryConfirm: 122,
  KitchenAutoReady: 123,
  KitchenAutoBumpPriority: 124,
} as const;

/**
 * InteractionType constants - matches InteractionLogEnums.cs / InteractionType enum.
 */
export const INTERACTION_TYPE = {
  Add: 1,
  Modify: 2,
  Delete: 3,
  View: 4,
  Print: 5,
  Discharged: 6,
  Transfer: 7,
  Undelete: 8,
} as const;

/**
 * Log entity type constants - matches LogInteractionDAL.cs constants.
 * Used by GET /api/LogInteraction/{facilityId}/{entityType}/{entityId}
 * to select which SQL query to run.
 */
export const LOG_ENTITY_TYPE = {
  Patient: 1,
  Item: 2,
  MenuHeader: 3,
  GlobalMPGroup: 4,
  GlobalAdjGroup: 5,
  FoodAllergy: 6,
  DietType: 7,
  Consistency: 8,
  SpecialDevices: 9,
  Facility: 10,
  User: 11,
} as const;

/**
 * Maps table config keys to log entity types for fetching change history.
 * Only tables with dedicated log SQL queries are included.
 */
export const TABLE_KEY_TO_LOG_ENTITY: Record<string, number> = {
  dietTypes: LOG_ENTITY_TYPE.DietType,
  consistencies: LOG_ENTITY_TYPE.Consistency,
  foodAllergies: LOG_ENTITY_TYPE.FoodAllergy,
  specialDevices: LOG_ENTITY_TYPE.SpecialDevices,
  adjustmentGlobalGroups: LOG_ENTITY_TYPE.GlobalAdjGroup,
  mealPatternGlobalGroups: LOG_ENTITY_TYPE.GlobalMPGroup,
};

/**
 * Audit log config for Tables CRUD operations.
 * Maps table config keys to the INTERACTION_LOCATION values for add/modify (addMod)
 * and delete (dataGrid), plus the foreign key field name used in the log payload.
 *
 * Tables without a matching INTERACTION_LOCATION enum value are omitted;
 * no log will be written for those tables.
 */
export interface TableAuditLogConfig {
  /** INTERACTION_LOCATION for save (add/modify) operations */
  addModLocation: number;
  /** INTERACTION_LOCATION for delete operations (typically the DataGrid location) */
  deleteLocation: number;
  /** The foreign key field name on LogInteraction that identifies this entity (e.g. 'fKey_Diet_Type') */
  fkField: string;
  /** When true, fires a View (type=4) log at `addModLocation` the
   *  moment the Modify dialog opens — ports the WinForms pattern in
   *  `ucDietech_Special_Devices.vb:332-342` and the dialog-level View
   *  writes in `frmDietech_SpecialNeeds_AddMod.vb` / `_LabelColors_` /
   *  `_FoodAllergy_` / `_Portions_` / `_DRIGroup_` / `_DiningRoom_`.
   *  Opt-in because many tables don't audit View today — enable it
   *  only when porting a specific table's audit behavior verbatim. */
  logViewOnOpen?: boolean;
}

export const TABLE_KEY_TO_AUDIT_LOG: Record<string, TableAuditLogConfig> = {
  dietTypes: {
    addModLocation: INTERACTION_LOCATION.DietTypeAddMod,
    deleteLocation: INTERACTION_LOCATION.DietTypeDatagrid,
    fkField: 'fKey_Diet_Type',
    // frmDietech_LabelColors_AddMod.Show writes a View log at
    // DietTypeAddMod the moment the Modify dialog opens, for a row that
    // is not Detached (.vb:672-684).
    logViewOnOpen: true,
    // Note: the Add / Modify rows themselves are written server-side by
    // DietTypeController.AddMod (`SERVER_LOGS_ADDMOD` in Tables.tsx) —
    // only it knows which per-facility override rows survived
    // normalization, and only it holds the pre-update row the diff needs.
    // Delete is written from the page, via the formatter below.
  },
  consistencies: {
    addModLocation: INTERACTION_LOCATION.ConsistencyAddMod,
    deleteLocation: INTERACTION_LOCATION.ConsistencyDatagrid,
    fkField: 'fKey_Consist',
    // `frmDietech_LabelColors_AddMod.Show` writes a View row (loc 37,
    // type 4) the moment the Modify dialog opens on an existing
    // consistency — AddMod:666-680. Add and Modify are client-side here,
    // through `CONSISTENCY_FORMATTER`; Delete and Replace are written
    // server-side inside their transactions (`ConsistencyMenuDAL`).
    logViewOnOpen: true,
  },
  foodAllergies: {
    addModLocation: INTERACTION_LOCATION.FoodAllergyAddMod,
    deleteLocation: INTERACTION_LOCATION.FoodAllergyDataGrid,
    fkField: 'fKey_Food_Allergy',
    // frmDietech_Food_Allergy_AddMod.Show:58-70 writes a View log the
    // moment the Modify dialog opens (only when the row already exists —
    // the New path leaves `_rowLoad` detached and skips it, which is what
    // `logViewOnOpen` does too). Add and Modify are written server-side by
    // `FoodAllergyDAL.AddMod` (see SERVER_LOGS_ADDMOD in Tables.tsx)
    // because the Modify note needs the adjustment tree to name a changed
    // link; View carries an empty note, so the client can write it.
    logViewOnOpen: true,
  },
  allergyGroups: {
    // ucDietech_AllergyGroup writes Add, View, Modify AND Delete all at
    // AllergyGroupDataGrid (48) - it never uses AllergyGroupAddMod (49),
    // because the user control owns the save rather than a separate AddMod
    // form (.vb:87-96, :100-127, :153-163). This pointed at 49, so every
    // Add/Modify row landed at a location this table never queries.
    addModLocation: INTERACTION_LOCATION.AllergyGroupDataGrid,
    deleteLocation: INTERACTION_LOCATION.AllergyGroupDataGrid,
    fkField: 'fKey_Prd_Allergy_Group',
    // _btnOpen_ItemClick writes a View row the moment the Modify form
    // opens, for a row that is not Detached (.vb:100-110).
    logViewOnOpen: true,
  },
  specialDevices: {
    addModLocation: INTERACTION_LOCATION.SpecialDevicesAddMod,
    deleteLocation: INTERACTION_LOCATION.SpecialDevicesDataGrid,
    fkField: 'fKey_Spec_Devices',
    // ucDietech_Special_Devices.vb:332-342 writes a View log when
    // the Modify dialog opens — WinForms-verbatim parity.
    logViewOnOpen: true,
  },
  specialNeeds: {
    addModLocation: INTERACTION_LOCATION.SpecialNeedsAddMod,
    deleteLocation: INTERACTION_LOCATION.SpecialNeedsDataGrid,
    fkField: 'fKey_Special_Needs',
    // `frmDietech_SpecialNeeds_AddMod.Show` writes a View row at
    // SpecialNeedsAddMod (61) the moment the Modify dialog opens, for a
    // row that is not Detached (.vb:92-106). Add and Modify are written
    // from the page through `SPECIAL_NEED_FORMATTER`; Delete and Replace
    // are written server-side inside their own transactions
    // (`SpecialNeedDAL`), which is why `specialNeeds` is in
    // `SERVER_LOGS_DELETE` in Tables.tsx.
    logViewOnOpen: true,
  },
  feedingStatus: {
    addModLocation: INTERACTION_LOCATION.FacilityFeedingStatusAddMod,
    deleteLocation: INTERACTION_LOCATION.FacilityFeedingStatusDataGrid,
    fkField: 'fKey_FAC_Status',
    // frmDietech_FeedingStatus_AddMod.vb:17-27 writes a View log the
    // moment the Modify dialog opens.
    logViewOnOpen: true,
  },
  itemTypes: {
    addModLocation: INTERACTION_LOCATION.ItemTypeAddMod,
    deleteLocation: INTERACTION_LOCATION.ItemTypeDataGrid,
    fkField: 'fKey_Item_Prd_ItemType',
    // ucDietech_ItemType._btnOpen_ItemClick writes a View row at
    // ItemTypeAddMod the moment the Modify dialog opens (.vb:302-313).
    // Add and Modify are written from the page through
    // `ITEM_TYPE_FORMATTER`; Delete and Replace are written server-side
    // inside their own transactions (`ItemTypeDAL`), which is why
    // `itemTypes` is in `SERVER_LOGS_DELETE` in Tables.tsx.
    logViewOnOpen: true,
  },
  serviceItems: {
    addModLocation: INTERACTION_LOCATION.ServiceItemAddMod,
    deleteLocation: INTERACTION_LOCATION.ServiceItemDataGrid,
    fkField: 'fKey_Prd_Service_Item',
    // frmDietech_Service_Item_AddMod.Show writes a View row at
    // ServiceItemAddMod the moment the Modify dialog opens (.vb:18-26).
    // Add and Modify come from the page through SERVICE_ITEM_FORMATTER;
    // Delete and Replace are written server-side inside their
    // transactions, which is why serviceItems is in SERVER_LOGS_DELETE.
    logViewOnOpen: true,
  },
  foodGroups: {
    addModLocation: INTERACTION_LOCATION.FoodGroupAddMod,
    deleteLocation: INTERACTION_LOCATION.FoodGroupDataGrid,
    fkField: 'fKey_Prd_Food_Group',
  },
  adjustmentGlobalGroups: {
    addModLocation: INTERACTION_LOCATION.GlobalAdjustmentGroupAddMod,
    deleteLocation: INTERACTION_LOCATION.GlobalAdjustmentGroupDataGrid,
    // frmDietech_AdjustmentGlobalGroup_AddMod.vb:14-26 writes a View row
    // at GlobalAdjustmentGroupAddMod the moment the Modify dialog opens,
    // with an empty note. Guarded on `RowState <> Detached`, so New does
    // not write one — the same split `logViewOnOpen` makes. Delete is
    // written server-side by GlobalAdjustmentDAL.GlobalGroupDelete, which
    // is why adjustmentGlobalGroups is in SERVER_LOGS_DELETE.
    logViewOnOpen: true,
    fkField: 'fKey_Prd_Adjustment_Global_Group',
  },
  mealPatternGlobalGroups: {
    addModLocation: INTERACTION_LOCATION.GlobalMealPatternGroupAddMod,
    deleteLocation: INTERACTION_LOCATION.GlobalMealPatternGroupDataGrid,
    // frmDietech_MealPatternGlobalGroup_AddMod.vb:15-26 writes a View row
    // at GlobalMealPatternGroupAddMod when the Modify dialog opens, with
    // an empty note. Guarded on `RowState <> Detached`, so New writes
    // none — the split `logViewOnOpen` makes. Add and Modify notes come
    // from MEAL_PATTERN_GLOBAL_GROUP_FORMATTER; Delete is written
    // server-side, hence SERVER_LOGS_DELETE in Tables.tsx.
    logViewOnOpen: true,
    fkField: 'fKey_Prd_Meal_Pattern_Global_Group',
  },
  portions: {
    addModLocation: INTERACTION_LOCATION.Portion_AddMod,
    deleteLocation: INTERACTION_LOCATION.Portion_AddMod, // unused: the table has no Delete
    fkField: 'fKey_Prd_Portion',
    // `frmDietech_Portions_AddMod.Show` writes a View row at
    // Portion_AddMod (78) the moment the dialog opens, before anything is
    // edited (.vb:15-26). Portions has no Add and no Delete — it is a
    // fixed list — so View and Modify are the only two rows this table
    // ever writes, and `PORTION_FORMATTER` builds the Modify note.
    logViewOnOpen: true,
  },
  driGroups: {
    addModLocation: INTERACTION_LOCATION.DRIGroup_AddMod,
    deleteLocation: INTERACTION_LOCATION.DRIGroup_DataGrid,
    fkField: 'fKey_RC_DRI_Group',
  },
  diningRooms: {
    addModLocation: INTERACTION_LOCATION.DiningRoom_AddMod,
    deleteLocation: INTERACTION_LOCATION.DiningRoom_DataGrid,
    fkField: 'fKey_LayoutDiningRoom',
  },
  employees: {
    addModLocation: INTERACTION_LOCATION.Employee_DataGrid,
    deleteLocation: INTERACTION_LOCATION.Employee_DataGrid,
    fkField: 'fKey_Employee',
  },
  tubeFeedProducts: {
    addModLocation: INTERACTION_LOCATION.TubeProduct_AddMod,
    deleteLocation: INTERACTION_LOCATION.TubeProduct_DataGrid,
    fkField: 'fKey_Products',
    // ucDietech_TubeFeedProducts._btnOpen_ItemClick writes a View row at
    // TubeProduct_AddMod the moment the Modify dialog opens (.vb:258-266).
    // Add and Modify come from the page through
    // TUBE_FEED_PRODUCT_FORMATTER; Delete is written server-side by
    // TubeFeedDAL.DeleteProduct, which is why tubeFeedProducts is in
    // SERVER_LOGS_DELETE in Tables.tsx.
    logViewOnOpen: true,
  },
  // Note Types are deliberately absent. `ucDietech_NoteType.vb` and
  // `frmDietech_NoteType_AddMod.vb` contain no InteractionData at all —
  // neither Add, Modify nor Delete is logged — so the React page writes
  // nothing either.
  //
  // The entry that used to sit here also pointed `fkField` at
  // `fKey_Notes`, which is a NOTE's primary key, not a note type's.
};

// ─── Tables that write NO interaction log ─────────────────────────────
//
// Deliberately absent from the map above, each verified against its
// WinForms source (zero `InteractionData` / `InsertLogInteraction`):
//
//   itemUnits         ucDietech_Item_Unit.vb
//   noteTypes         ucDietech_NoteType.vb, frmDietech_NoteType_AddMod.vb
//   medicationEffects ucDietech_MedicationEffects.vb,
//                     frmDietech_MedicationsEffect_AddMod.vb
//   menuCategories    the only menu-category logs live on
//                     frmDietech_Facility_MenuCategoryLayout, a different
//                     surface, and they log at FacilityDataGrid against
//                     FKey_Facility — not this grid, not this entity
//   doctors           ucDietech_Prescribers.vb
//   printerCategories ucDietech_PrinterCategory.vb
//   garnish           ucDietech_Garnish.vb
//   productionAreas   ucDietech_ProductionAreas.vb
//   materialColors    no WinForms counterpart
//
// All seven of the last entries used to be registered here with
// `fkField: 'fKey_Item'` and a "reuse generic FK" note. That was invented
// rather than ported, and actively harmful: tblLog_Interaction.FKey_Item
// points at tblItem, so writing a printer category's PKey into it is a
// foreign-key violation — the same failure that made creating a note type
// error out. Do not re-add a table here without finding its
// InteractionData block in the WinForms source first.

// Item Units are deliberately absent from the map above.
// `ucDietech_Item_Unit.vb` writes NO interaction log at all — not on Add,
// Modify, Delete or Replace — so neither does the React page.
//
// It used to be registered with `fkField: 'fKey_Item'`, borrowing the
// generic item FK the way the other note-only tables do. That is harmless
// where the FK is nullable and unconstrained, but tblLog_Interaction's
// FKey_Item points at tblItem: writing an item UNIT's PKey into it fails
// the foreign key, and creating a unit errored out on the log rather than
// on the save.
